import { useEffect, useState } from "react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, AlertTriangle } from "lucide-react";
import { useGraphite } from "@/components/Graphite";

// Every destructive action (docs/ACCOUNT_LIFECYCLE_PLAN.md slice A): what happens, "This can't be undone", and the
// person's password, which the backend checks before doing anything (a wrong one answers 403). `confirmName`: the
// name to type as well (a server's). `warning`: for an action that can be undone for a while (Cancel plan). The
// password is cleared whenever the dialog closes.
export default function ConfirmDestructive({ open, onOpenChange, title, children, action = "Confirm", confirmName,
  pending, onConfirm, warning = "This can't be undone.", cancelLabel = "Cancel" }) {
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  useEffect(() => { if (!open) { setPassword(""); setName(""); } }, [open]);
  const ready = password.length > 0 && (!confirmName || name.trim() === confirmName);
  const g = useGraphite();
  // the handoff: the consequences, then "This can't be undone." in the warning colour, and the confirm button stays
  // the primary one, disabled until a password is typed
  const danger = g ? "text-gr-warning" : "text-red-500";
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="confirm-destructive-dialog">
        <form onSubmit={(e) => { e.preventDefault(); if (ready && !pending) onConfirm(password, name.trim()); }}>
          <AlertDialogHeader>
            <AlertDialogTitle>{title}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className={g ? "space-y-4 text-base text-gr-ink-secondary" : "space-y-2 text-sm"}>
                {children}
                <p className={g ? `flex items-center gap-1.5 ${danger} text-sm` : `flex items-center gap-1.5 ${danger} font-medium`}>
                  <AlertTriangle className={g ? "w-3.5 h-3.5 shrink-0" : "w-4 h-4 shrink-0"} /> {warning}
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 my-4">
            {confirmName && (
              <div className="space-y-1.5">
                <Label htmlFor="confirm-name">Type <span className={g ? "font-semibold" : "font-mono"}>{confirmName}</span> to confirm</Label>
                <Input id="confirm-name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off"
                  data-testid="confirm-name" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="confirm-password">Your password</Label>
              <Input id="confirm-password" type="password" autoComplete="current-password" value={password}
                onChange={(e) => setPassword(e.target.value)} autoFocus={!confirmName} data-testid="confirm-password" />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel type="button">{cancelLabel}</AlertDialogCancel>
            <Button type="submit" variant={g ? "default" : "destructive"} disabled={!ready || pending} data-testid="confirm-destructive">
              {pending && <Loader2 className="w-4 h-4 animate-spin" />}{action}
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
